import { type FC, useState } from "react";
import { Button, MenuItem, Stack, TextField, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useTranslation } from "react-i18next";
import {
  AudienceMatchType,
  type AudienceConditionInputType,
  type AudienceFieldType,
  type AudienceFilterInputType,
} from "@/types/graphql/partner.generated";
import {
  AUDIENCE_FIELDS,
  defaultCondition,
  type MarketingLimits,
} from "@/partner/entities/marketing";
import { AUDIENCE_MATCH_KEYS } from "../model/audienceLabels";
import { AudienceConditionRow } from "./AudienceConditionRow";

const MATCHES: AudienceMatchType[] = [
  AudienceMatchType.AllType,
  AudienceMatchType.AnyType,
];

interface AudienceFilterBuilderProps {
  value: AudienceFilterInputType;
  onChange: (value: AudienceFilterInputType) => void;
  disabled: boolean;
  /** Пределы с бэкенда; null, пока их нет: тогда число условий не ограничено. */
  limits: MarketingLimits | null;
  /** Поля условий; по умолчанию поля аудитории (AUDIENCE_FIELDS). */
  fields?: readonly AudienceFieldType[];
  /** Письма выше шага условия цепочки; по умолчанию писем нет. */
  emailNodes?: readonly { id: string; label: string }[];
}

const NO_EMAIL_NODES: readonly { id: string; label: string }[] = [];

/**
 * Фильтр зрителей рассылки: «все условия» или «любое», список условий и
 * «Добавить условие» (до limits.audienceConditions). Без условий фильтр берёт всех
 * зрителей платформы.
 */
export const AudienceFilterBuilder: FC<AudienceFilterBuilderProps> = ({
  value,
  onChange,
  disabled,
  limits,
  fields = AUDIENCE_FIELDS,
  emailNodes = NO_EMAIL_NODES,
}) => {
  const { t } = useTranslation();
  // У условий нет id: стабильные ключи строк держим рядом, иначе после
  // удаления из середины поля поиска соседних строк перепутались бы.
  const [rowKeys, setRowKeys] = useState<string[]>(() =>
    value.conditions.map(() => crypto.randomUUID())
  );
  const keys =
    rowKeys.length === value.conditions.length
      ? rowKeys
      : value.conditions.map((_c, index) => rowKeys[index] ?? String(index));

  const setConditions = (conditions: AudienceConditionInputType[]) =>
    onChange({ ...value, conditions });

  const onAdd = () => {
    setRowKeys([...keys, crypto.randomUUID()]);
    setConditions([
      ...value.conditions,
      defaultCondition(fields[0] ?? AUDIENCE_FIELDS[0]),
    ]);
  };

  const onRemove = (index: number) => {
    setRowKeys(keys.filter((_key, i) => i !== index));
    setConditions(value.conditions.filter((_c, i) => i !== index));
  };

  const onUpdate = (index: number, condition: AudienceConditionInputType) =>
    setConditions(
      value.conditions.map((c, i) => (i === index ? condition : c))
    );

  return (
    <Stack spacing={2}>
      <TextField
        select
        label={t("broadcasts.audience.match")}
        value={value.match}
        onChange={(event) =>
          onChange({ ...value, match: event.target.value as AudienceMatchType })
        }
        disabled={disabled}
        sx={{ maxWidth: { sm: 260 } }}
      >
        {MATCHES.map((match) => (
          <MenuItem key={match} value={match}>
            {t(AUDIENCE_MATCH_KEYS[match])}
          </MenuItem>
        ))}
      </TextField>
      {value.conditions.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          {t("broadcasts.audience.everyone")}
        </Typography>
      )}
      {value.conditions.length > 0 && (
        // Контейнер для раскладки строк условий по ширине карточки, а не
        // окна (AudienceConditionRow, финальное ревью C2).
        <Stack spacing={2} sx={{ containerType: "inline-size" }}>
          {value.conditions.map((condition, index) => (
            <AudienceConditionRow
              key={keys[index]}
              condition={condition}
              onChange={(next) => onUpdate(index, next)}
              onRemove={() => onRemove(index)}
              disabled={disabled}
              limits={limits}
              fields={fields}
              emailNodes={emailNodes}
            />
          ))}
        </Stack>
      )}
      <Stack direction="row">
        <Button
          startIcon={<AddIcon />}
          onClick={onAdd}
          disabled={
            disabled ||
            (limits !== null &&
              value.conditions.length >= limits.audienceConditions)
          }
        >
          {t("broadcasts.audience.addCondition")}
        </Button>
      </Stack>
    </Stack>
  );
};
